import React, { Suspense } from "react";

// 7HAI-1224: every route chunk is content-hashed, so a deploy removes the
// files an already-open tab still holds references to. lazyWithRetry reloads
// once when that happens, instead of letting the app die on the error
// boundary — see the module note for why once, and why never a loop.
import { lazyWithRetry } from "../utils/lazyWithRetry";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import NotFound from "./NotFound";

import { useLocalStorage } from "../hooks/useLocalStorage";
import {
  LS_SESSION_ID,
  LS_USER,
  LS_COMPANY_LICENSE,
  LS_COMPANY_DPA,
} from "../constants/storageKeys";
import type { UserResponseModel } from "../api/models/UserResponseModel";
import {
  hasPendingAgreement,
  type CompanyLicenseInfo,
  type CompanyDpaInfo,
} from "./pendingAgreement";

import SuspenseFallback from "../components/loading/SuspenseFallback";
import { RowReviewProvider } from "../components/upload/RowReviewProvider";
import { MissingInfoProvider } from "../components/upload/MissingInfoProvider";

// Pages are lazy-loaded so each top-level area ships as its own route chunk
// instead of being pulled into the initial bundle. The <Suspense> boundaries
// below render SuspenseFallback while a chunk is in flight.
const LoginPage = lazyWithRetry(() => import("../pages/login/LoginPage"));
const AgreementsPage = lazyWithRetry(() => import("../pages/agreements/AgreementsPage"));
const Layout = lazyWithRetry(() => import("../pages/layouts/Layout"));

const MasterDashboardLayout = lazyWithRetry(() => import("../pages/master/MasterDashboardLayout"));
const MasterDashboard = lazyWithRetry(() => import("../pages/master/MasterDashboard"));
const CompaniesUsersPage = lazyWithRetry(() => import("../pages/master/CompaniesUsersPage"));
const ParserLearningsPage = lazyWithRetry(
  () => import("../pages/master/parser_learnings/ParserLearningsPage"),
);
const BillingPage = lazyWithRetry(() => import("../pages/master/billing/BillingPage"));
const PricingSettingsPage = lazyWithRetry(
  () => import("../pages/master/pricing/PricingSettingsPage"),
);

const LicensesLayout = lazyWithRetry(() => import("../pages/master/licenses/LicensesLayout"));
const LicensesPage = lazyWithRetry(() => import("../pages/master/licenses/LicensesPage"));
const CompanyDetails = lazyWithRetry(
  () => import("../pages/master/licenses/dependent_pages/CompanyDetails"),
);

const SettingsLayout = lazyWithRetry(() => import("../pages/master/settings/SettingsLayout"));
const SettingsPage = lazyWithRetry(() => import("../pages/master/settings/SettingsPage"));

const AdminDashboardLayout = lazyWithRetry(() => import("../pages/admin/AdminDashboardLayout"));
const AdminDashboard = lazyWithRetry(() => import("../pages/admin/AdminDashboard"));
const OwnCompanyPage = lazyWithRetry(() => import("../pages/admin/dependent_pages/OwnCompanyPage"));
const CalculateFilePage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/CalculateFilePage"),
);
// Dedicated row-classification review surface (7HAI-349) — its own lazy chunk,
// reached from a button on the calculate result. Reads the parse rows from
// RowReviewContext (published by CalculateFileCard).
const RowReviewPage = lazyWithRetry(() => import("../pages/admin/dependent_pages/RowReviewPage"));
// "Complete missing info" review surface (7HAI-369) — its own lazy chunk,
// reached from a CTA on the CAD calc result. Reads the flagged rows from
// MissingInfoContext (published by CalculateFileCard).
const MissingInfoPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/MissingInfoPage"),
);
const PriceListPage = lazyWithRetry(() => import("../pages/admin/dependent_pages/PriceListPage"));
// ISO 3834 estimation settings (7HAI-840) — quality multipliers, NDT rates,
// joint constants and pipe standard length. These were folded under the price
// table by 7HAI-529, which made `price_list` serve two unrelated things; they
// are their own route again. `price_list` still serves the price list, so no
// redirect is needed — nothing moved out from under an existing bookmark.
const Iso3834SettingsPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/Iso3834SettingsPage"),
);
// 7HAI-1223: per-material time multipliers. Its own page because the list is
// 45 sliders — it does not belong as a section of an existing settings screen.
const MaterialMultipliersPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/MaterialMultipliersPage"),
);
const CadLearningsPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/CadLearningsPage"),
);
const PtfAdjustmentPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/PtfAdjustmentPage"),
);
const PtfLibraryPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ptf/PtfLibraryPage"),
);
// 7HAI-2195: measured PTF from Trace — evidence beside the library, applied by choice.
const PtfMeasuredPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ptf/PtfMeasuredPage"),
);
const PtfCategoryPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ptf/PtfCategoryPage"),
);
// Read-only part lookup (7HAI-805): composes an elbow/tee/reducer total from
// the shared cells that feed it, since no elbow table exists to link to.
const PtfPartLookupPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ptf/PtfPartLookupPage"),
);
// Read-only cross-table overview (7HAI-805): pipe, fittings and weld seams for
// one material side by side, so a whole size range can be sanity-checked
// without opening three tables in turn.
const PtfOverviewPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ptf/PtfOverviewPage"),
);
// The rail every time-factor screen hangs off (7HAI-805). A PATHLESS layout
// route, so `ptf_adjustment` keeps its existing URL while still rendering
// inside the shell alongside its four siblings.
const PtfShell = lazyWithRetry(() => import("../components/ptf/PtfShell"));
// Pipeline stage admin (7HAI-342, parent 7HAI-335) — admin-only management of
// the company's pipeline stages (rename / reorder / won-lost flags / delete
// with project re-homing). Lazy like its sibling admin sub-pages.
const PipelineStagesPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/PipelineStagesPage"),
);
// NDT size bands (7HAI-823, parent 7HAI-816) — admin-only DN range + films-per-
// joint config. Its own lazy chunk like every other admin sub-page; the page
// itself redirects non-admins, since these rows change what bids cost.
const NdtBandsPage = lazyWithRetry(() => import("../pages/admin/dependent_pages/NdtBandsPage"));
// 7HAI-1104: the admin half of propose -> approve. Without it the proposals
// raised in 7HAI-1103 go nowhere.
const ConfigProposalsPage = lazyWithRetry(
  () => import("../pages/admin/dependent_pages/ConfigProposalsPage"),
);

// CRM pipeline board (7HAI-340) — a company-shared, team-wide surface, so it
// is mounted as its own top-level lazy chunk under the shared protected
// Layout rather than under a single role's dashboard.
const PipelineBoardPage = lazyWithRetry(() => import("../pages/projects/PipelineBoardPage"));
const ProjectDetailPage = lazyWithRetry(() => import("../pages/projects/ProjectDetailPage"));

const UserDashboardLayout = lazyWithRetry(() => import("../pages/user/UserDashboardLayout"));
const UserDashboard = lazyWithRetry(() => import("../pages/user/UserDashboard"));
// 7HAI-1099: the estimator's own Calculate route. Same batch panel the
// dashboard renders; it needed an address of its own to be navigable.
const UserCalculatePage = lazyWithRetry(() => import("../pages/user/CalculatePage"));
// 7HAI-1103: the estimator's read-only views of the four config surfaces. Their
// own pages rather than the admin ones behind a readOnly flag — on ISO 3834 the
// API accepts a user's write, so a missed flag there would be a real one.
const UserPriceListPage = lazyWithRetry(() => import("../pages/user/config/UserPriceListPage"));
const UserIso3834Page = lazyWithRetry(() => import("../pages/user/config/UserIso3834Page"));
const UserNdtBandsPage = lazyWithRetry(() => import("../pages/user/config/UserNdtBandsPage"));
const UserTimeFactorsPage = lazyWithRetry(() => import("../pages/user/config/UserTimeFactorsPage"));
// 7HAI-2233: mark runs, parts and joints on a plan drawing. One page for both
// shells, under each shell's calculate route, so "back" lands on the upload
// the drawing came from.
const DrawingTakeoffPage = lazyWithRetry(() => import("../pages/drawing/DrawingTakeoffPage"));
const BidListPage = lazyWithRetry(() => import("../pages/user/bids/BidListPage"));
const BidDetailPage = lazyWithRetry(() => import("../pages/user/bids/BidDetailPage"));
const BidComparePage = lazyWithRetry(() => import("../pages/user/bids/BidComparePage"));

// Exported for agreementsGate.test.tsx (7HAI-1094): the blank-page bug was this
// guard and AgreementsPage disagreeing about the same stored user, which only
// reproduces when both are mounted in one route tree. A test that cannot see the
// guard cannot see the bug.
export const ProtectedRoute = ({ children }: { children: React.JSX.Element }) => {
  const location = useLocation();
  const [sessionId] = useLocalStorage<string>(LS_SESSION_ID, "");
  const [user] = useLocalStorage<UserResponseModel | null>(LS_USER, null);
  // 7HAI-544: company EULA is company-wide — pass the stored company license so
  // the guard gates admins (accept) and non-admin users (wait for admin) too.
  const [companyLicense] = useLocalStorage<CompanyLicenseInfo>(LS_COMPANY_LICENSE, null);
  // 7HAI-972: same for the company DPA — a second company-wide agreement with
  // the same gate shape.
  const [companyDpa] = useLocalStorage<CompanyDpaInfo>(LS_COMPANY_DPA, null);

  if (!sessionId) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (
    hasPendingAgreement(user, companyLicense, companyDpa) &&
    location.pathname !== "/agreements"
  ) {
    return <Navigate to="/agreements" replace />;
  }
  return children;
};

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Public routes. The index route sends every visitor straight to
            the login page (matching TRACE, which renders its login surface at
            "/"); the stale landing page is gone. An authenticated user who
            logs in is routed onward to their role dashboard by LoginPage's
            post-auth ROLE_PATHS redirect. */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route
          path="/login"
          element={
            <Suspense fallback={<SuspenseFallback />}>
              <LoginPage />
            </Suspense>
          }
        />

        {/* Agreements is logged-in-only but lives outside the sidebar
            chrome — same standalone framing as /login. ProtectedRoute
            handles the redirect when terms_and_usage flags are missing. */}
        <Route
          path="/agreements"
          element={
            <ProtectedRoute>
              <Suspense fallback={<SuspenseFallback />}>
                <AgreementsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Protected layout with sidebar/header. RowReviewProvider wraps the
            whole protected subtree so the calculate result (CalculateFileCard)
            and the dedicated row-review page (sibling routes) share the parse
            rows across navigation (7HAI-349). */}
        <Route
          element={
            <ProtectedRoute>
              <RowReviewProvider>
                <MissingInfoProvider>
                  <Suspense fallback={<SuspenseFallback />}>
                    <Layout />
                  </Suspense>
                </MissingInfoProvider>
              </RowReviewProvider>
            </ProtectedRoute>
          }
        >
          {/* Master dashboard */}
          <Route
            path="/master_dashboard/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <MasterDashboardLayout />
              </Suspense>
            }
          >
            <Route index element={<MasterDashboard />} />
            <Route path="companies-users" element={<CompaniesUsersPage />} />
            <Route path="parser-learnings" element={<ParserLearningsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="pricing" element={<PricingSettingsPage />} />
          </Route>

          {/* Licenses */}
          <Route
            path="/licenses/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <LicensesLayout />
              </Suspense>
            }
          >
            <Route index element={<LicensesPage />} />
            <Route path="company" element={<CompanyDetails />} />
          </Route>

          {/* Settings */}
          <Route
            path="/settings/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <SettingsLayout />
              </Suspense>
            }
          >
            <Route index element={<SettingsPage />} />
            {/* 7HAI-1807: /settings/company, /settings/users (admin tabs). */}
            <Route path=":tab" element={<SettingsPage />} />
          </Route>

          {/* Projects / CRM pipeline board — shared across master/admin/user
              (it's a team pipeline), so it sits directly under the protected
              Layout, not inside a role dashboard. */}
          <Route
            path="/projects"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <PipelineBoardPage />
              </Suspense>
            }
          />
          {/* Project / opportunity detail (7HAI-341) — its own lazy chunk,
              reached from a board card. Same shared-Layout placement as the
              board itself. */}
          <Route
            path="/projects/:id"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <ProjectDetailPage />
              </Suspense>
            }
          />

          {/* Admin dashboard */}
          <Route
            path="/admin_dashboard/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <AdminDashboardLayout />
              </Suspense>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="own_company" element={<OwnCompanyPage />} />
            <Route path="calculate" element={<CalculateFilePage />} />
            <Route path="calculate/row-review" element={<RowReviewPage />} />
            <Route path="calculate/complete-missing" element={<MissingInfoPage />} />
            <Route path="calculate/drawing/:drawingId" element={<DrawingTakeoffPage />} />
            <Route path="price_list" element={<PriceListPage />} />
            <Route path="iso3834_settings" element={<Iso3834SettingsPage />} />
            <Route path="material_factors" element={<MaterialMultipliersPage />} />
            <Route path="cad-learnings" element={<CadLearningsPage />} />
            {/* Every time-factor screen renders inside PtfShell's rail. The
                layout route carries no path of its own, so the five URLs below
                are unchanged — including `ptf_adjustment`, which is a sibling
                of `ptf/` in the URL space but a sibling screen in the UI. */}
            <Route element={<PtfShell />}>
              <Route path="ptf_adjustment" element={<PtfAdjustmentPage />} />
              <Route path="ptf" element={<PtfLibraryPage />} />
              {/* Concrete paths first: neither is a table slug. */}
              <Route path="ptf/lookup" element={<PtfPartLookupPage />} />
              <Route path="ptf/overview" element={<PtfOverviewPage />} />
              <Route path="ptf/measured" element={<PtfMeasuredPage />} />
              <Route path="ptf/:category" element={<PtfCategoryPage />} />
            </Route>
            <Route path="pipeline-stages" element={<PipelineStagesPage />} />
            <Route path="ndt-bands" element={<NdtBandsPage />} />
            <Route path="proposals" element={<ConfigProposalsPage />} />
          </Route>

          <Route
            path="/user_dashboard/*"
            element={
              <Suspense fallback={<SuspenseFallback />}>
                <UserDashboardLayout />
              </Suspense>
            }
          >
            <Route index element={<UserDashboard />} />
            <Route path="calculate" element={<UserCalculatePage />} />
            <Route path="calculate/drawing/:drawingId" element={<DrawingTakeoffPage />} />
            <Route path="price-list" element={<UserPriceListPage />} />
            <Route path="iso3834" element={<UserIso3834Page />} />
            <Route path="ndt-bands" element={<UserNdtBandsPage />} />
            <Route path="time-factors" element={<UserTimeFactorsPage />} />
            {/* 7HAI-1105: the SAME page the admin route mounts. These rows are
                the estimator's own learned completions, so they get the real
                read-write screen rather than a read-only copy of it. */}
            <Route path="cad-mappings" element={<CadLearningsPage />} />
            <Route path="bids" element={<BidListPage />} />
            <Route path="bids/:bidId" element={<BidDetailPage />} />
            <Route path="bids/:bidId/compare" element={<BidComparePage />} />
            {/* 7HAI-824: every bid-detail tab is its own route, so a link to
                the Quote or the Revisions of a bid is shareable and Back walks
                back through the tabs. `compare` is a static segment and so
                out-ranks `:tab` in React Router's route ranking; an unknown
                segment falls back to the default tab rather than 404ing. */}
            <Route path="bids/:bidId/:tab" element={<BidDetailPage />} />
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
